import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { getStoredReferralCode } from '../lib/referral';
import { claimPendingInvite } from '../lib/pendingInvite';
import { canUseFeature, FeatureKey, TierKey, TIER_ORDER } from '../../supabase/functions/_shared/planFeatures';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  hasAdminAccess: boolean;
  hasResellerAccess: boolean;
  planTier: TierKey | null;
  userRole: string;
  accessReady: boolean;
  canUse: (feature: FeatureKey) => boolean;
  refreshAdminAccess: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null; mfaChallenge?: { factorId: string } | null }>;
  verifyMfa: (factorId: string, code: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [hasResellerAccess, setHasResellerAccess] = useState(false);
  const [planTier, setPlanTier] = useState<TierKey | null>(null);
  const [userRole, setUserRole] = useState('subscriber');
  const [ownerCheckedFor, setOwnerCheckedFor] = useState<string | null>(null);
  const [planCheckedFor, setPlanCheckedFor] = useState<string | null>(null);

  const refreshAdminAccess = useCallback(async () => {
    if (!user) {
      setHasAdminAccess(false);
      setHasResellerAccess(false);
      return;
    }
    const [owner, reseller] = await Promise.all([
      supabase.rpc('is_platform_owner'),
      supabase.rpc('has_reseller_plan'),
    ]);
    if (owner.error) console.error('[admin-access] RPC error:', owner.error.message);
    if (reseller.error) console.error('[reseller-access] RPC error:', reseller.error.message);
    setHasAdminAccess(!owner.error && owner.data === true);
    setHasResellerAccess(!reseller.error && reseller.data === true);
    setOwnerCheckedFor(user.id);
  }, [user]);

  useEffect(() => {
    refreshAdminAccess();
  }, [refreshAdminAccess]);

  useEffect(() => {
    if (!user) {
      setPlanTier(null);
      setUserRole('subscriber');
      return;
    }
    let active = true;
    Promise.all([
      supabase.from('user_subscriptions')
        .select('subscription_plans(tier_key)')
        .eq('user_id', user.id)
        .in('status', ['active', 'trialing'])
        .maybeSingle(),
      supabase.from('user_preferences').select('user_role').eq('user_id', user.id).maybeSingle(),
    ]).then(([sub, prefs]) => {
      if (!active) return;
      const key = (sub.data?.subscription_plans as { tier_key?: string } | null)?.tier_key;
      setPlanTier(key && (TIER_ORDER as readonly string[]).includes(key) ? (key as TierKey) : 'FREE');
      setUserRole(prefs.data?.user_role ?? 'subscriber');
      setPlanCheckedFor(user.id);
    });
    return () => { active = false; };
  }, [user]);

  const accessReady = !user || (ownerCheckedFor === user.id && planCheckedFor === user.id);
  const canUse = useCallback(
    (feature: FeatureKey) => canUseFeature(feature, { tier: planTier, role: userRole, isOwner: hasAdminAccess }),
    [planTier, userRole, hasAdminAccess],
  );

  useEffect(() => {
    let mounted = true;

    const autoClaimInvitation = (userId: string) => {
      (async () => {
        try {
          const result = await claimPendingInvite();
          if (result?.claimed) {
            console.info('[auto-claim] Invitation claimed for role:', result.role);
          }
        } catch (err) {
          console.error('[auto-claim] Unexpected error:', err);
        }
      })();
    };

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
      if (data.session?.user) autoClaimInvitation(data.session.user.id);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
      if (newSession?.user) autoClaimInvitation(newSession.user.id);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.code === 'mfa_required') {
        const factors = await supabase.auth.mfa.listFactors();
        const totpFactor = factors.data?.totp?.find(f => f.status === 'verified');
        if (totpFactor) {
          return { error: null, mfaChallenge: { factorId: totpFactor.id } };
        }
      }
      return { error: error?.message ?? null, mfaChallenge: null };
    }
    return { error: null, mfaChallenge: null };
  };

  const verifyMfa = async (factorId: string, code: string) => {
    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
    if (challengeError) return { error: challengeError.message };
    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });
    if (verifyError) return { error: verifyError.message };
    return { error: null };
  };

  const signUp = async (email: string, password: string) => {
    const referralCode = getStoredReferralCode();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: referralCode ? { data: { referral_code: referralCode } } : undefined,
    });
    if (!error) trackEvent('sign_up', { method: 'email' });
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, hasAdminAccess, hasResellerAccess, planTier, userRole, accessReady, canUse, refreshAdminAccess, signIn, verifyMfa, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be consumed within an AuthProvider.');
  }
  return context;
};
